/* eslint-disable @typescript-eslint/no-shadow */
import React, { useState } from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { colorPalette } from '@sharefiledev/antd-config';
import { DocumentGenerationStep } from '@sharefiledev/doc-gen-pilet-sdk';
import { esignDrawers } from '@sharefiledev/esign-pilet-sdk';
import { Button, Flex, Modal, theme, Tooltip, Typography } from 'antd';
import { PatchPayloadItem } from 'src/data/interfaces/APIRequest';
import { useAppStore } from '../../../common/store';
import { SendSignatureRequestFooterAnalyticsIds } from '../../../common/tracking/events';
import { DocumentMergeAction } from '../../../constants';
import { DocgenClient } from '../../../data/docgenClient';
import { handleError } from '../../../data/errorHandler';
import { templateDetailsPath } from '../../../routes';
import { t } from '../../../utils';
import {
	getDocumentGenerationStatus,
	hasSystemFilledFields,
} from '../../../utils/document';

const { Text } = Typography;

export const SignatureRequestFooter = ({ app }) => {
	const { token } = theme.useToken();
	const [modal, contextHolder] = Modal.useModal();
	const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);

	const {
		document,
		getDocument,
		documentTemplate,
		variables,
		templateMetadata,
		signatureMetadata,
		updateTemplateMetadata,
	} = useAppStore(state => ({
		document: state.document,
		getDocument: state.getDocument,
		documentTemplate: state.documentTemplate,
		variables: state.variables,
		templateMetadata: state.templateMetadata,
		signatureMetadata: state.signatureMetadata,
		updateTemplateMetadata: state.updateTemplateMetadata,
	}));

	const updateDocumentPayload = (path: string, value: any): PatchPayloadItem => ({
		op: 'replace',
		path,
		value,
	});

	const getRedirectPath = () => {
		if (templateMetadata?.source === 'esign') {
			return templateMetadata?.redirectPath || '/signature-requests';
		}

		return templateDetailsPath
			.replace(':templateRID', encodeURIComponent(documentTemplate.rid))
			.substring(1);
	};

	const VariablesWithNoValuesContent = () => {
		const variablesWithoutValues = variables
			.filter(variable => !variable.value)
			.map(variable => variable.name);
		return (
			<Flex
				vertical
				gap="middle"
				style={{ padding: `${token.paddingXS}px 0 ${token.paddingSM}px` }}
			>
				<Flex vertical gap="small">
					<Text style={{ marginBottom: 0 }}>
						{t('SendSignatureRequest.noVariableComponentInfoModal.subtitle')}
					</Text>
					<Text strong>{variablesWithoutValues.join(', ')}</Text>
				</Flex>
				<div>{t('SendSignatureRequest.noVariableComponentInfoModal.description')}</div>
			</Flex>
		);
	};

	const handleWarningForVariablesWithNoValues = async () => {
		return await modal.confirm({
			title: t('SendSignatureRequest.noVariableComponentInfoModal.title'),
			icon: <ExclamationCircleFilled />,
			content: VariablesWithNoValuesContent(),
			okText: t('SendSignatureRequest.noVariableComponentInfoModal.confirmButtonText'),
			cancelText: t('SendSignatureRequest.noVariableComponentInfoModal.cancelButtonText'),
		});
	};

	const handleConfirmationModalForGenerateDocument = async () => {
		return await modal.confirm({
			title: t('SendSignatureRequest.addFieldDataPanel.generateDocumentModal.title'),
			icon: <ExclamationCircleFilled />,
			content: t(
				'SendSignatureRequest.addFieldDataPanel.generateDocumentModal.description'
			),
			okText: t(
				'SendSignatureRequest.addFieldDataPanel.generateDocumentModal.confirmButtonText'
			),
			cancelText: t(
				'SendSignatureRequest.addFieldDataPanel.generateDocumentModal.cancelButtonText'
			),
		});
	};

	const handleStartOverButtonClick = () => {
		updateTemplateMetadata({ drawerCloseType: 'cancel' });
	};

	const generateDocumentAtPopulateFieldsStep = async document => {
		const hasEmptyVariables = variables?.some(variable => !variable.value);
		let payload: null | { mergeAction: DocumentMergeAction } = null;
		if (!hasEmptyVariables) {
			payload = {
				mergeAction: DocumentMergeAction.GenerateSignerFields,
			};
		}
		try {
			await DocgenClient.mergeDocument(document.rid, payload);
			try {
				await getDocument(document.rid);
				updateTemplateMetadata({ drawerCloseType: 'complete' });
				openEsignDrawer();
			} catch {
				handleError(t('errorMessages.getDocumentError'));
				return;
			}
		} catch {
			handleError(t('errorMessages.generateDocumentError'));
		} finally {
			setIsGeneratingDocument(false);
		}
	};

	const openEsignDrawer = () => {
		setTimeout(() => {
			app.showDrawer(esignDrawers.quickSendForSignatureDrawer, {
				params: {
					documentRID: document.rid,
					redirectPath: getRedirectPath(),
					containerRID: documentTemplate.containerRID,
					documentGenerationStatus: getDocumentGenerationStatus(
						signatureMetadata.numberOfsteps
					),
				},
			});
		}, 0);
	};

	const onNextButtonClick = async () => {
		if (signatureMetadata.currentStep === DocumentGenerationStep.POPULATE_FIELDS) {
			const hasEmptyVariables = variables?.some(variable => !variable.value);
			if (hasEmptyVariables) {
				const isConfirmed = await handleWarningForVariablesWithNoValues();
				if (!isConfirmed) {
					return;
				}
			}
			const isConfirmed = await handleConfirmationModalForGenerateDocument();
			if (!isConfirmed) {
				return;
			}
			setIsGeneratingDocument(true);
			generateDocumentAtPopulateFieldsStep(document);
		}
	};

	if (
		!signatureMetadata ||
		signatureMetadata.currentStep !== DocumentGenerationStep.POPULATE_FIELDS
	) {
		return null;
	}

	return (
		<Flex
			justify="space-between"
			align="center"
			style={{
				background: colorPalette.neutral1,
				flexDirection: 'row-reverse',
				visibility: document ? 'visible' : 'hidden',
				width: '100%',
			}}
		>
			{contextHolder}

			<div>
				<Tooltip
					placement="right"
					title={
						signatureMetadata.isEditInProgress
							? t('SendSignatureRequest.addFieldDataPanel.signersFieldEdited')
							: ''
					}
					trigger={['hover', 'focus']}
				>
					<Button
						type="primary"
						onClick={onNextButtonClick}
						data-analytics-id={
							SendSignatureRequestFooterAnalyticsIds.NextStepSelectRecipients
						}
						disabled={signatureMetadata.isEditInProgress || isGeneratingDocument}
						loading={isGeneratingDocument}
						data-testid="docgen-generate-document"
					>
						{t('SendSignatureRequest.footer.generateDocument')}
					</Button>
				</Tooltip>
			</div>

			{templateMetadata?.source === 'esign' &&
				hasSystemFilledFields(document?.fields) && (
					<Button
						onClick={() => updateTemplateMetadata({ drawerCloseType: 'cancel' })}
						data-analytics-id={SendSignatureRequestFooterAnalyticsIds.ChangeTemplate}
					>
						{t('SendSignatureRequest.footer.changeTemplate')}
					</Button>
				)}
		</Flex>
	);
};
